import React, { useLayoutEffect, useMemo, useRef, useState, useCallback } from "react";
import styled from "styled-components";

import { useSticky } from "react-table-sticky";
import { useBlockLayout, useTable } from "react-table";
import { useMediaQuery } from "react-responsive";
import DbRow from "./dbRow";
import { Checkbox, Button } from "@material-ui/core";
import { getPost, getUserFacingApiErrorMessage } from "../utils/commHelper";
import { saveColumnOrder, loadColumnOrder, clearColumnOrder } from "./filterUtils";

const moveArrayItem = (arr, from, to) => {
  arr.splice(to, 0, arr.splice(from, 1)[0]);
};

const DbTableData = ({
  columnsRes,
  dataRes,
  getData,
  totalCount,
  setSaving,
  columnsMap,
  refRes,
  tableName,
  setOpenErrorToast,
  setDataRes,
  take,
  // Reference lookups inside the cells read from the public API; the inline cell edit is a
  // write and goes to the VPN-only host.
  apiTarget,
  writeApiTarget,
  deleteList,
  setDeleteList,
  deleteAll,
  setDeleteAll,
  isReadOnly = false,
  userPermissions = { canRead: true, canWrite: false, canDelete: false, canAdd: false },
}) => {
  const isDesktopOrLaptop = useMediaQuery({
    query: "(min-device-width: 1000px)",
  });

  const [columns, setColumns] = useState([]);
  const [pendingNum, setPendingNum] = useState(0);
  const [columnOrderChanged, setColumnOrderChanged] = useState(false);
  const [draggedColumnIndex, setDraggedColumnIndex] = useState(null);
  const [dragOverColumnIndex, setDragOverColumnIndex] = useState(null);

  // Apply default column ordering
  const applyDefaultColumnOrder = useCallback((cols) => {
    const newColumns = [...cols];
    const titleIndex = newColumns.findIndex((e) => e.name === "title");

    if (titleIndex > -1) {
      moveArrayItem(newColumns, titleIndex, 0);
      moveArrayItem(
        newColumns,
        newColumns.findIndex((e) => e.name === "_id"),
        1
      );
      moveArrayItem(
        newColumns,
        newColumns.findIndex((e) => e.name === "_createdDate"),
        2
      );
      moveArrayItem(
        newColumns,
        newColumns.findIndex((e) => e.name === "_updatedDate"),
        3
      );
    }
    return newColumns;
  }, []);

  // Apply saved column order if available
  const applySavedColumnOrder = useCallback((cols) => {
    if (!tableName) return cols;
    
    const savedOrder = loadColumnOrder(tableName);
    if (!savedOrder || savedOrder.length === 0) {
      return applyDefaultColumnOrder(cols);
    }

    // Sort columns based on saved order
    const orderedColumns = [];
    const remainingColumns = [...cols];
    
    for (const colName of savedOrder) {
      const colIndex = remainingColumns.findIndex((c) => c.name === colName);
      if (colIndex > -1) {
        orderedColumns.push(remainingColumns[colIndex]);
        remainingColumns.splice(colIndex, 1);
      }
    }
    
    // Add any remaining columns not in saved order
    return [...orderedColumns, ...remainingColumns];
  }, [tableName, applyDefaultColumnOrder]);

  useLayoutEffect(() => {
    const orderedColumns = applySavedColumnOrder(columnsRes);

    setColumns(
      orderedColumns.map((item, index) => {
        // First column (index 0) should always be sticky on desktop
        if (index === 0 && isDesktopOrLaptop) {
          return {
            Header: item.name,
            accessor: item.name,
            sticky: "left",
          };
        } else {
          return {
            Header: item.name,
            accessor: item.name,
          };
        }
      })
    );
  }, [columnsRes, applySavedColumnOrder, isDesktopOrLaptop]);

  // Save current column order
  const handleSaveColumnOrder = useCallback(() => {
    const currentOrder = columns.map((c) => c.accessor);
    saveColumnOrder(tableName, currentOrder);
    setColumnOrderChanged(false);
  }, [columns, tableName]);

  // Reset to default column order
  const handleResetColumnOrder = useCallback(() => {
    clearColumnOrder(tableName);
    const defaultOrdered = applyDefaultColumnOrder([...columnsRes]);
    setColumns(
      defaultOrdered.map((item, index) => ({
        Header: item.name,
        accessor: item.name,
        sticky: index === 0 && isDesktopOrLaptop ? "left" : undefined,
      }))
    );
    setColumnOrderChanged(false);
  }, [tableName, columnsRes, applyDefaultColumnOrder, isDesktopOrLaptop]);

  // Handle column drag start
  const handleDragStart = useCallback((e, columnIndex) => {
    setDraggedColumnIndex(columnIndex);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', columnIndex);
    // Add a slight delay to allow drag image to be set
    setTimeout(() => {
      e.target.style.opacity = '0.5';
    }, 0);
  }, []);

  // Handle column drag over
  const handleDragOver = useCallback((e, columnIndex) => {
    if (draggedColumnIndex === null) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverColumnIndex !== columnIndex) {
      setDragOverColumnIndex(columnIndex);
    }
  }, [draggedColumnIndex, dragOverColumnIndex]);

  // Handle column drop
  const handleDrop = useCallback((e, dropIndex) => {
    if (draggedColumnIndex === null) return;
    e.preventDefault();
    
    if (draggedColumnIndex !== dropIndex) {
      const newColumns = [...columns];
      const draggedColumn = newColumns[draggedColumnIndex];
      
      // Remove dragged column
      newColumns.splice(draggedColumnIndex, 1);
      
      // Insert at new position
      newColumns.splice(dropIndex, 0, draggedColumn);
      
      // Ensure first column is sticky on desktop
      if (isDesktopOrLaptop) {
        newColumns.forEach((col, index) => {
          if (index === 0) {
            col.sticky = "left";
          } else {
            delete col.sticky;
          }
        });
      }
      
      setColumns(newColumns);
      setColumnOrderChanged(true);
    }
    
    setDraggedColumnIndex(null);
    setDragOverColumnIndex(null);
    e.target.style.opacity = '1';
  }, [draggedColumnIndex, columns, isDesktopOrLaptop]);

  // Handle drag end
  const handleDragEnd = useCallback((e) => {
    e.target.style.opacity = '1';
    setDraggedColumnIndex(null);
    setDragOverColumnIndex(null);
  }, []);

  const updateToAws = async (data) => {
    return await getPost(
      "/insertBulkBalance",
      { table: tableName, values: [data] },
      writeApiTarget
    );
  };
  const defaultColumn = React.useMemo(
    () => ({
      width: 380,
    }),
    []
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable(
      {
        columns,
        data: dataRes || [],
        defaultColumn,
      },
      useBlockLayout,
      useSticky
    );

  const pending = useRef(false);

  const handleScroll = (e) => {
    const { scrollHeight, scrollTop, clientHeight } = e.target;
    // Use a threshold instead of strict equality: scrollTop is often a
    // fractional (sub-pixel) value on high-DPI displays / browser zoom, so
    // `scrollHeight - scrollTop === clientHeight` rarely holds exactly and the
    // "load more" fetch would silently never fire. Trigger slightly early.
    const bottom = scrollHeight - scrollTop - clientHeight <= 100;

    if (!pending.current && bottom && dataRes.length < totalCount.current) {
      pending.current = true;
      getData().then(() => {
        pending.current = false;
        setPendingNum(pendingNum + 1);
      });
    }
  };

  const _renderRows = useMemo(() => {
    return (
      <div {...getTableBodyProps()} className="body">
        {rows.map((row, rowIndex) => {
          const id = tableName === "users" ? row.values.user : row.values._id;
          const isDelete = deleteList.includes(id);

          prepareRow(row);
          return (
            <div
              key={`${id}_${rowIndex}`}
              {...row.getRowProps()}
              style={{
                ...row.getRowProps().style,
                backgroundColor: deleteAll || isDelete ? "#c1e4fe" : isReadOnly ? "#fafafa" : "white",
              }}
              className="tr"
            >
              {row.cells.map((item, index) => {
                const columnKey = item?.column?.id;
                const columnData = columnsMap.current[columnKey];
                const rowProps = item.getCellProps();
                
                // Column is read-only if it's a system field OR if user doesn't have write permission
                let cellReadOnly = isReadOnly || [
                  "_id",
                  "_createdDate",
                  "_updatedDate",
                ].includes(columnKey);
                if (
                  !cellReadOnly &&
                  tableName === "users" &&
                  columnKey === "user"
                ) {
                  cellReadOnly = true;
                }

                return (
                  <DbRow
                    rowIndex={rowIndex}
                    deleteAll={deleteAll}
                    key={`${id}_${columnKey}`}
                    isDelete={isDelete}
                    rowProps={rowProps}
                    columnKey={columnKey}
                    index={index}
                    refRes={refRes}
                    columnData={columnData}
                    dataRes={dataRes}
                    item={item}
                    isReadOnly={cellReadOnly}
                    canDelete={userPermissions.canDelete}
                    onCheckChange={userPermissions.canDelete ? (newValue) => {
                      const newDeleteList = [...deleteList];
                      const checked = newValue.target.checked;

                      if (deleteAll) {
                        setDeleteAll(false);
                      }

                      if (!checked) {
                        const itemIndex = newDeleteList.findIndex(
                          (e) => e === id
                        );
                        newDeleteList.splice(itemIndex, 1);
                      } else {
                        newDeleteList.push(id);
                      }
                      setDeleteList(newDeleteList);
                    } : null}
                    onChange={!isReadOnly ? (newValue) => {
                      setSaving(true);

                      const newItem = {};
                      Object.keys(row.values).forEach((itemKey) => {
                        // Handle multi-ref fields (arrays)
                        if (columnsMap.current[itemKey]?.isMultiRef && Array.isArray(row.values[itemKey])) {
                          newItem[itemKey] = row.values[itemKey].map(item => item._id || item);
                        } else if (columnsMap.current[itemKey]?.reference && row.values[itemKey]?._id) {
                          // Handle single reference fields
                          newItem[itemKey] = row.values[itemKey]._id;
                        } else {
                          newItem[itemKey] = row.values[itemKey];
                        }
                      });
                      // Handle the changed column
                      if (columnData?.isMultiRef && Array.isArray(newValue)) {
                        // Multi-ref: extract array of IDs
                        newItem[columnData?.name] = newValue.map(item => item._id || item);
                      } else if (columnData?.reference && newValue?._id) {
                        // Single reference: extract ID
                        newItem[columnData?.name] = newValue._id;
                      } else {
                        newItem[columnData?.name] = newValue;
                      }

                      updateToAws(newItem)
                        .then((res) => {
                          if (
                            res.status !== 200 ||
                            res.data.errors?.length > 0
                          ) {
                            setOpenErrorToast(
                              res.status !== 200
                                ? "Connection problem"
                                : res.data.errors
                            );
                          } else {
                            const newData = [...dataRes];
                            const itemIndex = newData.findIndex(
                              (e) =>
                                (tableName === "users" ? e.user : e._id) === id
                            );
                            newData[itemIndex][columnKey] = newValue;
                            setDataRes(newData);
                            setSaving(false);
                          }
                        })
                        .catch((e) => { const message = getUserFacingApiErrorMessage(e); if (message) setOpenErrorToast(message); });
                    } : null}
                    apiTarget={apiTarget}
                  />
                );
              })}
            </div>
          );
        })}
        {dataRes.length < totalCount.current && (
          <div style={{ margin: "10px" }}>{`Load more ${take}...`}</div>
        )}
      </div>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- deps intentionally scoped to table-data inputs; adding the per-render closures (prepareRow/getTableBodyProps/dataRes/etc.) would recompute the whole body on every render and change render behavior
  }, [rows, deleteList, isReadOnly, userPermissions]);
  const _renderColumns = useMemo(() => {
    return (
      <div className="header">
        {headerGroups.map((headerGroup) => (
          <div {...headerGroup.getHeaderGroupProps()} className="tr">
            {headerGroup.headers.map((column, columnIndex) => (
              <div
                key={`${column.value}_${columnIndex}`}
                {...column.getHeaderProps()}
                className="th"
                draggable={true}
                onDragStart={(e) => handleDragStart(e, columnIndex)}
                onDragOver={(e) => handleDragOver(e, columnIndex)}
                onDrop={(e) => handleDrop(e, columnIndex)}
                onDragEnd={handleDragEnd}
                style={{
                  ...column.getHeaderProps().style,
                  cursor: 'grab',
                  backgroundColor: dragOverColumnIndex === columnIndex ? '#e3f2fd' : 
                                   draggedColumnIndex === columnIndex ? '#f5f5f5' : 
                                   columnIndex === 0 ? '#fff' : '#fff',
                  opacity: draggedColumnIndex === columnIndex ? 0.5 : 1,
                  transition: 'background-color 0.2s',
                  zIndex: columnIndex === 0 ? 12 : 10,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {/* Only show delete checkbox if user has delete permission */}
                    {columnIndex === 0 && userPermissions.canDelete && (
                      <Checkbox
                        checked={deleteAll}
                        onChange={(event) => {
                          setDeleteAll(event.target.checked);
                        }}
                      />
                    )}
                  <span>{column.render("Header")}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>

                    
                    <span style={{ 
                      fontSize: '12px', 
                      color: '#999',
                      userSelect: 'none'
                    }}>
                      ⋮⋮
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  }, [headerGroups, deleteAll, userPermissions.canDelete, draggedColumnIndex, dragOverColumnIndex, handleDragStart, handleDragOver, handleDrop, handleDragEnd, setDeleteAll]);

  return (
    <Styles isReadOnly={isReadOnly}>
      {/* Column order controls */}
      <ColumnOrderControls>
        <ColumnOrderHint>
          💡 Drag column headers to reorder
        </ColumnOrderHint>
        <Button
          size="small"
          style={{ marginRight: "10px" }}
          onClick={handleSaveColumnOrder}
          disabled={!columnOrderChanged}
        >
          Save Column Order
        </Button>
        <Button
          size="small"
          onClick={handleResetColumnOrder}
        >
          Reset to Default
        </Button>
        {isReadOnly && (
          <ReadOnlyIndicator>View Only</ReadOnlyIndicator>
        )}
      </ColumnOrderControls>
      <div
        onScroll={handleScroll}
        {...getTableProps()}
        className="table sticky"
        style={{ width: "100%", height: "100%" }}
      >
        {_renderColumns}
        {_renderRows}
      </div>
    </Styles>
  );
};

export default DbTableData;

const Styles = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  
  ${({ isReadOnly }) => isReadOnly && `
    .td {
      cursor: not-allowed;
    }
  `}

  .table {
    border: 1px solid #ddd;
    flex: 1;

    .tr {
      :last-child {
        .td {
          border-bottom: 0;
        }
      }
    }

    .th,
    .td {
      width: 380px !important;
      padding: 5px;
      border-bottom: 1px solid #ddd;
      border-right: 1px solid #ddd;
      background-color: #fff;
      overflow: hidden;

      :last-child {
        border-right: 0;
      }
    }

    .th {
      user-select: none;
      
      &:active {
        cursor: grabbing;
      }
    }

    &.sticky {
      overflow: scroll;

      .header,
      .footer {
        position: sticky;
        z-index: 10;
        width: fit-content;
      }

      .header {
        top: 0;
        box-shadow: 0px 3px 3px #ccc;
      }

      .footer {
        bottom: 0;
        box-shadow: 0px -3px 3px #ccc;
      }

      .body {
        position: relative;
        z-index: 0;
      }

      [data-sticky-td],
      [data-sticky-th] {
        position: sticky;
      }

      [data-sticky-last-left-td],
      [data-sticky-last-left-th] {
        box-shadow: 2px 0px 3px #ccc;
      }

      [data-sticky-first-right-td],
      [data-sticky-first-right-th] {
        box-shadow: -2px 0px 3px #ccc;
      }

      /* All headers should be above all body cells */
      .header .th {
        z-index: 10;
        background-color: #fff;
      }

      /* First column header should be on top of other headers */
      .header .th:first-child,
      .header [data-sticky-th]:first-child {
        z-index: 12;
        background-color: #fff;
      }

      /* All body cells should be below headers */
      .body .td {
        background-color: #fff;
      }

      /* First column cells should be on top of other cells but below headers */
      .body .td:first-child,
      .body [data-sticky-td]:first-child {
        z-index: 2;
        background-color: #fff;
      }
    }
  }
`;

const ColumnOrderControls = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 10px;
  background-color: #f9f9f9;
  border-bottom: 1px solid #ddd;
`;

const ReadOnlyIndicator = styled.span`
  margin-left: auto;
  padding: 4px 12px;
  background-color: #fff3cd;
  color: #856404;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
`;

const ColumnOrderHint = styled.span`
  margin-right: auto;
  padding: 4px 8px;
  color: #666;
  font-size: 12px;
  font-style: italic;
`;
